import { motion } from 'framer-motion';
import {
  Package,
  Users,
  Database,
  Building2,
  ArrowRight,
  Layers,
  CheckCircle,
  Target,
  TrendingUp,
  Zap,
  BarChart3,
  AlertCircle,
  DollarSign,
  Lightbulb,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHero from '../../components/PageHero';
import { getPageHeroImageUrl } from '../../config/cloudflareAssets';

export default function ResourceAuditPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const resourceCategories = [
    {
      icon: Building2,
      title: 'Physical Resources',
      description: 'Tangible assets you can touch, see, and physically control',
      examples: [
        {
          item: 'Facilities & Real Estate',
          questions: ['Location advantages?', 'Capacity utilization?', 'Owned vs leased?'],
        },
        {
          item: 'Equipment & Machinery',
          questions: ['Age and condition?', 'Technology level?', 'Utilization rate?'],
        },
        {
          item: 'Technology Infrastructure',
          questions: ['Cloud vs on-premise?', 'Scalability?', 'Technical debt?'],
        },
        {
          item: 'Inventory & Materials',
          questions: ['Turnover rate?', 'Obsolescence risk?', 'Supply chain reliability?'],
        },
      ],
      considerations:
        'Physical resources are capital-intensive and slow to acquire/change. Assess whether you have the right type, quantity, and quality—and whether they create strategic value or just meet baseline needs.',
    },
    {
      icon: Users,
      title: 'Human Resources',
      description: 'People, skills, knowledge, and organizational culture',
      examples: [
        {
          item: 'Talent & Expertise',
          questions: ['Key skill gaps?', 'Bench strength?', 'Retention rates?'],
        },
        {
          item: 'Leadership Quality',
          questions: ['Succession plans?', 'Decision-making ability?', 'Strategic thinking?'],
        },
        {
          item: 'Organizational Culture',
          questions: ['Innovation vs execution?', 'Risk tolerance?', 'Collaboration level?'],
        },
        {
          item: 'Training & Development',
          questions: ['Investment per employee?', 'Skill building programs?', 'Career paths?'],
        },
      ],
      considerations:
        'Human resources are your most important and most difficult to manage. Skills can be developed but take time. Culture is powerful but hard to change. Focus on fit with strategy and competency requirements.',
    },
    {
      icon: Database,
      title: 'Financial Resources',
      description: 'Capital, cash flow, and financial capacity to invest and operate',
      examples: [
        {
          item: 'Cash & Liquidity',
          questions: ['Runway at current burn?', 'Access to credit?', 'Working capital needs?'],
        },
        {
          item: 'Profitability & Margins',
          questions: ['Gross vs net margins?', 'Unit economics?', 'Path to profitability?'],
        },
        {
          item: 'Investment Capacity',
          questions: ['Available for growth?', 'ROI on past investments?', 'Cost of capital?'],
        },
        {
          item: 'Financial Flexibility',
          questions: ['Debt vs equity?', 'Covenant restrictions?', 'Scenario resilience?'],
        },
      ],
      considerations:
        'Financial resources are the fuel for strategy. Insufficient capital constrains growth; excess capital can lead to waste. Assess adequacy relative to strategic ambitions and competitive dynamics.',
    },
    {
      icon: Package,
      title: 'Intangible Resources',
      description: 'Non-physical assets that often create the most sustainable value',
      examples: [
        {
          item: 'Brand & Reputation',
          questions: ['Awareness levels?', 'Brand equity value?', 'Perception vs reality?'],
        },
        {
          item: 'Intellectual Property',
          questions: ['Patents, trademarks?', 'Trade secrets?', 'Defensibility?'],
        },
        {
          item: 'Data & Insights',
          questions: ['Proprietary data assets?', 'Analytics capabilities?', 'Data monetization?'],
        },
        {
          item: 'Relationships & Networks',
          questions: ['Key partnerships?', 'Channel strength?', 'Ecosystem position?'],
        },
      ],
      considerations:
        "Intangibles are often undervalued but create disproportionate advantage. They're hard to build, easy to damage, and difficult for competitors to replicate. Often the source of core competencies.",
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'Internal Assessment', path: '/internal-assessment' },
              { label: 'Resource Audit' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Layers className="w-4 h-4 mr-2" />
            Internal Assessment Tool 3 of 3
          </span>
        }
        title="Know what your strategy will really need"
        subtitle="Compare your ambitions with the people, assets, funding, and knowledge available to deliver them. Find the resource gaps before they become execution problems."
      >
        <Link
          to="/sign-up"
          className="public-primary-button inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90"
        >
          Audit your resources
        </Link>
      </PageHero>

      {/* Why Resource Audit Matters */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Make the plan fit the resources behind it</h2>
              <p className="text-xl text-gray-600">
                A strong strategy needs more than ambition. An honest resource audit reveals what
                you can use today, what needs development, and where trade-offs are unavoidable.
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  icon: Target,
                  title: 'Grounds Strategy in Reality',
                  description:
                    "You can't execute a strategy you can't resource. Many strategic plans fail not because of poor ideas but insufficient resources. Auditing clarifies what's possible.",
                  impact: 'Realistic, executable strategy vs. wishful thinking',
                },
                {
                  icon: TrendingUp,
                  title: 'Identifies Resource Gaps',
                  description:
                    "Gap between current resources and strategic requirements reveals where investment is needed. Can't grow without sales capacity? Can't innovate without R&D budget? Audit surfaces gaps.",
                  impact: 'Prioritized investment and development needs',
                },
                {
                  icon: Zap,
                  title: 'Reveals Underutilized Assets',
                  description:
                    'Many organizations sit on valuable but underutilized resources—data not analyzed, facilities at low capacity, skills not deployed. Audit identifies opportunities to leverage existing assets.',
                  impact: 'Improved ROI and resource efficiency',
                },
                {
                  icon: BarChart3,
                  title: 'Enables Better Allocation',
                  description:
                    "Can't optimize what you don't measure. Audit provides baseline to reallocate resources from low-value to high-value uses, improving overall productivity.",
                  impact: 'Strategic resource allocation and portfolio management',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <item.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold">{item.title}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-xs text-gray-700">
                      <span className="font-semibold text-green-700">Impact:</span> {item.impact}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Four Resource Categories */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">The Four Resource Categories</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive inventory across all resource types
            </p>
          </motion.div>
          <div className="space-y-8 max-w-6xl mx-auto">
            {resourceCategories.map((cat, i) => (
              <motion.div
                key={i}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="public-card bg-white p-8 rounded-xl border border-gray-200"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-blue-600 p-3 rounded-lg">
                    <cat.icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">{cat.title}</h3>
                    <p className="text-sm text-blue-700 font-medium">{cat.description}</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  {cat.examples.map((ex, j) => (
                    <div key={j} className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-semibold text-gray-900 mb-2">{ex.item}</p>
                      <ul className="space-y-1">
                        {ex.questions.map((q, k) => (
                          <li key={k} className="text-xs text-gray-600 flex items-start">
                            <CheckCircle className="w-3 h-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
                            {q}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-blue-700">Key Consideration:</span>{' '}
                    {cat.considerations}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Conduct Resource Audit */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">
                How to Conduct an Effective Resource Audit
              </h2>
              <p className="text-xl text-gray-600">
                A systematic process for comprehensive resource assessment
              </p>
            </motion.div>
            <div className="space-y-6">
              {[
                {
                  step: '1',
                  title: 'Define Scope and Objectives',
                  description:
                    'Start with why: Are you auditing to identify gaps for growth? Optimize current resources? Support M&A due diligence? Clear objectives shape what to measure and how deep to go.',
                  tip: 'Narrow scope initially. Full organization audit is overwhelming. Start with one business unit or one resource type, learn, then expand.',
                },
                {
                  step: '2',
                  title: 'Inventory Physical & Financial Resources',
                  description:
                    "Start with tangible resources because they're easier to count and value. List all physical assets (facilities, equipment, inventory) and financial resources (cash, credit lines, investments).",
                  tip: 'Use balance sheet as starting point but go deeper: age of equipment, location of facilities, utilization rates, not just book value.',
                },
                {
                  step: '3',
                  title: 'Assess Human Resources',
                  description:
                    "Map skills, not just headcount. What capabilities exist? What's the depth (one expert vs. team)? What critical roles lack succession? Survey engagement and retention risk.",
                  tip: 'Create skill matrix: list critical skills vertically, employees horizontally. Identify single points of failure where only one person has a critical skill.',
                },
                {
                  step: '4',
                  title: 'Evaluate Intangible Assets',
                  description:
                    'Hardest to audit but often most valuable. Assess brand value (awareness, sentiment), IP portfolio (patents, trademarks, trade secrets), data assets, and relationship capital.',
                  tip: 'For brand value, use customer surveys. For relationships, map key partners/customers and assess switching costs. Quantify what competitors would pay for these assets.',
                },
                {
                  step: '5',
                  title: 'Assess Quality, Not Just Quantity',
                  description:
                    'Ten mediocre salespeople ≠ ten great salespeople. 1000 low-engagement customers ≠ 1000 loyal advocates. Evaluate condition, effectiveness, and strategic value of each resource.',
                  tip: "Use rating system: A (strategic asset), B (solid performer), C (adequate), D (needs improvement), F (liability). Prioritize A's and fix or exit F's.",
                },
                {
                  step: '6',
                  title: 'Identify Gaps vs. Strategic Needs',
                  description:
                    "Compare current resources to what strategy requires. If growth strategy targets enterprise customers but sales team only sells to SMBs—that's a gap requiring investment.",
                  tip: 'Create gap matrix: Required resources in rows, current state in columns. Red = critical gap, Yellow = developing, Green = sufficient. Prioritize reds.',
                },
                {
                  step: '7',
                  title: 'Benchmark Against Competitors',
                  description:
                    "Absolute resource levels matter less than relative position. You don't need most R&D spending if you're more efficient. Benchmark quantity and quality against direct competitors.",
                  tip: 'Use industry reports, job postings (proxy for team size), financial filings (public companies), and partner intelligence to estimate competitor resources.',
                },
                {
                  step: '8',
                  title: 'Create Action Plan',
                  description:
                    "Don't stop at inventory. Every audit should produce priorities: resources to build, optimize, redeploy, or divest. Assign owners and timelines.",
                  tip: 'Categorize actions: Acquire (hire, buy, partner), Develop (train, build), Optimize (better utilization), Divest (sell, shut down, outsource).',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-700 mb-3">{item.description}</p>
                      <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-blue-700">Pro Tip:</span> {item.tip}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Resource Optimization Strategies */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Resource Optimization Strategies</h2>
              <p className="text-xl text-gray-600">
                Turn audit insights into action with these four approaches
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  icon: TrendingUp,
                  title: 'Acquire Missing Resources',
                  description:
                    'When gaps are critical to strategy, you must acquire. Hire talent, buy equipment, raise capital, license IP, or partner. Speed matters—slow acquisition means delayed execution.',
                  actions: [
                    'Hiring campaigns',
                    'Capital raises',
                    'M&A/partnerships',
                    'Technology licensing',
                  ],
                },
                {
                  icon: Lightbulb,
                  title: 'Develop Existing Resources',
                  description:
                    'Building internal capability is slower but creates ownership and fit. Train employees, develop IP, build brand, accumulate data. Best for long-term competitive advantage.',
                  actions: [
                    'Training programs',
                    'R&D investments',
                    'Brand building',
                    'Process improvement',
                  ],
                },
                {
                  icon: BarChart3,
                  title: 'Optimize Utilization',
                  description:
                    'Squeeze more value from existing resources. Increase factory utilization, redeploy underutilized talent, monetize data, sweat assets harder. Often fastest ROI.',
                  actions: [
                    'Capacity management',
                    'Talent redeployment',
                    'Asset monetization',
                    'Waste elimination',
                  ],
                },
                {
                  icon: DollarSign,
                  title: 'Divest Non-Strategic Resources',
                  description:
                    "Resources that don't support strategy drain attention and capital. Sell non-core assets, shut down obsolete facilities, exit unprofitable geographies, outsource commodities.",
                  actions: ['Asset sales', 'Business exits', 'Outsourcing', 'Simplification'],
                },
              ].map((strat, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="bg-blue-600 p-2 rounded-lg">
                      <strat.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold">{strat.title}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{strat.description}</p>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs font-semibold text-gray-900 mb-2">Common Actions:</p>
                    <ul className="space-y-1">
                      {strat.actions.map((a, i) => (
                        <li key={i} className="text-xs text-gray-600">
                          • {a}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Common Resource Audit Mistakes</h2>
              <p className="text-xl text-gray-600">Avoid these traps that undermine audit value</p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  mistake: 'Focusing Only on Tangible Assets',
                  description:
                    'Auditing just physical and financial resources while ignoring intangibles. Yet brand, IP, data, and relationships often create more strategic value than equipment.',
                  fix: 'Force 50/50 split: spend equal audit time on tangible and intangible resources. Intangibles are harder to measure but more important.',
                },
                {
                  mistake: 'Counting Without Evaluating Quality',
                  description:
                    'Reporting "We have 100 salespeople" without assessing their effectiveness. Quantity ≠ capability. Ten A-players > fifty C-players.',
                  fix: 'Add quality metrics: sales per rep, productivity benchmarks, skill assessments. Focus on effective resources, not just resource count.',
                },
                {
                  mistake: 'Static Snapshot vs. Dynamic View',
                  description:
                    "Auditing resources at one point in time without considering trends. Are resources growing or shrinking? Improving or degrading? What's the velocity?",
                  fix: "Include trend data: 3-year history and forward projections. Resources declining despite stable headcount? That's a warning sign.",
                },
                {
                  mistake: 'Not Linking to Strategy',
                  description:
                    'Creating comprehensive inventory but never asking: "Do these resources support our strategy?" Resource audit without strategic lens is just accounting.',
                  fix: 'For each resource category, ask: "How does this enable our strategy? Where are gaps?" Strategic relevance > completeness.',
                },
                {
                  mistake: 'Forgetting Opportunity Cost',
                  description:
                    "Resources allocated to Area A can't be used in Area B. Audit shows what you have but not if they're in the right place doing the right things.",
                  fix: 'Assess allocation: % of resources in core vs. non-core activities. High % in non-core suggests misallocation despite adequate total resources.',
                },
                {
                  mistake: 'Audit Without Action',
                  description:
                    'Spending weeks creating detailed resource inventory, filing it away, and never acting on findings. Audit value is in decisions made, not reports written.',
                  fix: 'Build action plan into audit process. Every gap identified must have: recommended action, owner, timeline, and success metric.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-red-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">{item.mistake}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">✓ Fix:</span> {item.fix}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Put the framework to work */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                YOUR NEXT STEP IN STRATPLAN
              </p>
              <h2 className="text-3xl font-bold mb-5">
                Connect your available resources to your priorities
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bring this framework into your company’s plan. Work through the questions, record
                your decisions, and connect them to the strategy you are building.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">01</p>
                <h3 className="text-xl font-bold mb-3">Take a clear inventory</h3>
                <p className="text-gray-600">
                  Document the resources your company can use across people, physical assets,
                  finances, and intangibles.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">02</p>
                <h3 className="text-xl font-bold mb-3">Find the gaps</h3>
                <p className="text-gray-600">
                  Compare current capacity and quality with the demands of your strategic
                  objectives.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">03</p>
                <h3 className="text-xl font-bold mb-3">Choose the next investment</h3>
                <p className="text-gray-600">
                  Decide what to acquire, develop, reallocate, or stop using before you commit to
                  initiatives.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Related Strategic Tools</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/internal-assessment/swot-analysis"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <CheckCircle className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-blue-600">SWOT Analysis</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Connect resources to strengths and weaknesses
                </p>
                <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/internal-assessment/core-competencies"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Zap className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-purple-600">Core Competencies</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Identify which resources create competitive advantage
                </p>
                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/foundation/strategic-objectives"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Target className="w-8 h-8 text-indigo-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-indigo-600">Strategic Objectives</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Align resource allocation with strategic goals
                </p>
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/internal-assessment/core-competencies"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50"
              >
                ← Previous: Core Competencies
              </Link>
              <Link
                to="/strategy-formulation"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Next Step: Strategy Formulation <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start your plan */}
      <section className="public-section public-cta py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-5">
            Audit your resources in StratPlan
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Decide what to acquire, develop, reallocate, or stop using before you commit to
            initiatives. Start with your company’s context and build a plan you can put to work.
          </p>
          <Link
            to="/sign-up"
            className="public-primary-button inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700"
          >
            Audit your resources <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
